import { useCallback, useMemo, useRef } from 'react';

import { usePlayerStore } from '../components/PlayerContextProvider';
import { usePlayerTransportController } from '../components/PlayerTransportProvider';
import { usePlayerPause } from './usePlayerPause';
import { usePlayerPlay } from './usePlayerPlay';

export const usePlayerSeekChange = () => {
  const { transportController } = usePlayerTransportController();
  const previousPlayingRef = useRef<boolean>();
  const videoElement = usePlayerStore((state) => state.videoElement);
  const playing = usePlayerStore((state) => state.playing);
  const setSeeking = usePlayerStore((state) => state.setSeeking);
  const setCurrentTime = usePlayerStore((state) => state.setCurrentTime);
  const { onPlayerPlay } = usePlayerPlay();
  const { onPlayerPause } = usePlayerPause();

  const onPlayerSeekChange = useCallback(
    (value: number) => {
      if (transportController) {
        transportController.seek(value);
        return;
      }

      if (!videoElement) {
        return;
      }

      videoElement.currentTime = value;
      setCurrentTime(value);
    },
    [transportController, videoElement, setCurrentTime],
  );

  const onPlayerSeekEnd = useCallback(() => {
    if (transportController) {
      transportController.endSeek();
      return;
    }

    setSeeking(false);

    if (previousPlayingRef.current && videoElement) {
      onPlayerPlay();
      setCurrentTime(videoElement.currentTime);
    }
  }, [transportController, setSeeking, videoElement, setCurrentTime, onPlayerPlay]);

  const onPlayerSeekStart = useCallback(() => {
    if (transportController) {
      transportController.beginSeek();
      return;
    }

    setSeeking(true);

    previousPlayingRef.current = playing;

    if (videoElement && !videoElement.paused) {
      onPlayerPause();
      setCurrentTime(videoElement.currentTime);
    }
  }, [transportController, setSeeking, videoElement, playing, setCurrentTime, onPlayerPause]);

  return useMemo(
    () => ({
      onPlayerSeekChange,
      onPlayerSeekEnd,
      onPlayerSeekStart,
    }),
    [onPlayerSeekChange, onPlayerSeekEnd, onPlayerSeekStart],
  );
};
